import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { panicAll, PanicAllDomainResult } from 'src/Api/exchanges';
import { SYSTEM_ALL_STATE_KEY } from 'src/Common/hooks/useSystemKillSwitch';

/**
 * Cross-domain master PANIC. Arms every domain's halt/lockout keys and pauses
 * their rules in Mongo from the always-on manager, so it still works when the
 * per-domain control pods are scaled to 0.
 *
 * Destructive and irreversible in one click, so it follows the codebase's panic
 * confirm pattern (`GroupPodControls`): the icon only opens a dialog, and the
 * red CTA inside it is what fires. The per-domain result is rendered as-is —
 * a domain the backend did not report is simply not listed.
 */
const formatRemaining = (n: number | null | undefined) =>
  typeof n === 'number' ? String(n) : '?';

export const PanicAllButton = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<PanicAllDomainResult[] | null>(null);

  const panicMutation = useMutation(panicAll, {
    mutationKey: 'system:panic-all',
    onSuccess: data => {
      setResults(data);
      qc.invalidateQueries([SYSTEM_ALL_STATE_KEY]);
      // The endpoint pauses rules in Mongo on BOTH directions plus the sniper,
      // and those lists poll slowly (entry-rules every 60s) — without this the
      // tables keep painting paused rules as ARMED for up to a minute.
      qc.invalidateQueries('entry-rules');
      qc.invalidateQueries('unwinding-rules');
      qc.invalidateQueries(['sniper-groups']);
      // The master panic also puts the global sniper control in Apagado.
      qc.invalidateQueries(['sniper-control']);
    }
  });

  // Never traps the operator: the dialog always closes, even mid-flight. The
  // manager can be wedged during the very incident this button exists for, and
  // a modal backdrop that cannot be dismissed would block the per-domain PANIC
  // buttons too. The mutation keeps running; its failure still surfaces through
  // the app-wide MutationCache toast.
  const handleClose = () => {
    setOpen(false);
    setResults(null);
    if (!panicMutation.isLoading) panicMutation.reset();
  };

  return (
    <>
      {/*
        The request can take up to ~2 min (the sniper leg waits for a VERIFIED
        cancel), and the dialog may be closed meanwhile: the icon itself says a
        panic is still running, and re-opening shows its progress.
      */}
      <Tooltip
        title={
          panicMutation.isLoading
            ? 'PANIC ALL in progress — click to see it'
            : 'PANIC ALL prediction domains'
        }
        placement="right"
      >
        <IconButton
          onClick={() => {
            // A mid-flight close leaves the previous run's rows behind; showing
            // them on re-open costs two clicks in an emergency.
            setResults(null);
            setOpen(true);
          }}
          size="small"
          aria-label="Panic all prediction domains"
          sx={{
            color: theme => theme.palette.error.main,
            border: theme => `1px solid ${theme.palette.error.main}`,
            borderRadius: '5px',
            '& > svg': { fontSize: '1.1rem' }
          }}
        >
          {panicMutation.isLoading ? (
            <CircularProgress
              size={16}
              color="error"
              data-testid="panic-all-in-flight"
            />
          ) : (
            <ReportProblemIcon />
          )}
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          PANIC ALL — every prediction domain
        </DialogTitle>
        <DialogContent>
          {results ? (
            <Stack spacing={1}>
              {results.length === 0 ? (
                <Typography sx={{ fontSize: '0.8rem', color: 'grey.500' }}>
                  The backend reported no per-domain result.
                </Typography>
              ) : (
                results.map(r => {
                  const keys = r.keysArmed?.length ?? 0;
                  return (
                    <Box key={r.domain}>
                      <Typography
                        sx={{ fontSize: '0.8rem', color: 'common.white' }}
                      >
                        {r.domain} — {keys} key
                        {keys === 1 ? '' : 's'} armed,{' '}
                        {typeof r.rulesPaused === 'number'
                          ? `${r.rulesPaused} rule${r.rulesPaused === 1 ? '' : 's'} paused`
                          : '🚨 rules NOT confirmed paused — possibly still ARMED'}
                        {r.delegated ? ' (delegated)' : ''}
                      </Typography>
                      {r.verified === false && (
                        <Typography
                          data-testid={`panic-all-unverified-${r.domain}`}
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            color: 'error.main'
                          }}
                        >
                          🚨 {r.domain}: NOT verified — K{' '}
                          {formatRemaining(r.kalshiRemaining)} / P{' '}
                          {formatRemaining(r.polymarketRemaining)} remaining
                        </Typography>
                      )}
                      {r.errors?.length > 0 && (
                        <Typography
                          sx={{ fontSize: '0.75rem', color: 'error.main' }}
                        >
                          {r.errors.join('; ')}
                        </Typography>
                      )}
                    </Box>
                  );
                })
              )}
            </Stack>
          ) : panicMutation.isLoading ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
              <CircularProgress size={28} color="error" />
              <Typography
                data-testid="panic-all-waiting"
                sx={{ fontSize: '0.8rem', color: 'grey.500', textAlign: 'center' }}
              >
                PANIC ALL running — waiting for every domain&apos;s result. The
                sniper verifies its cancel against the venues, so this can take
                up to ~2 min. Closing this dialog does not stop it.
              </Typography>
            </Stack>
          ) : (
            <DialogContentText component="div">
              This arms the halt + lockout keys of <b>every</b> prediction
              domain (opener, unwinder, opportunities, matching, sniper) and
              pauses their rules in the database.
              <br />
              <br />
              It runs from the always-on manager, so it applies even to domains
              whose control plane is currently down. Rules stay paused until an
              operator re-arms them.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {results ? (
            <Button onClick={handleClose}>Close</Button>
          ) : (
            <>
              <Button onClick={handleClose} color="inherit">
                {panicMutation.isLoading ? 'Close' : 'Cancel'}
              </Button>
              <Button
                onClick={() => panicMutation.mutate()}
                color="error"
                variant="contained"
                startIcon={<ReportProblemIcon />}
                disabled={panicMutation.isLoading}
              >
                PANIC EVERYTHING
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
